import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import supabase from '../supabase';

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTasks = useCallback(async () => {
    const { data, error } = await supabase
      .from('todos')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) { setError(error.message); setLoading(false); return; }
    setTasks(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    const channel = supabase
      .channel('tasks-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'todos' }, fetchTasks)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [fetchTasks]);

  async function toggleComplete(task) {
    const newStatus = task.status === 'done' ? 'pending' : 'done';
    const { error } = await supabase
      .from('todos')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', task.id);
    if (error) { setError(error.message); return; }
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
  }

  const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const pending = tasks.filter(t => t.status !== 'done');
  const done    = tasks.filter(t => t.status === 'done' && new Date(t.updated_at) >= oneWeekAgo);

  const pendingGroups = groupByTag(pending);

  if (loading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>Tasks</h1>
        <p>{pending.length} pending · {done.length} completed</p>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Pending */}
        <div className="d-card">
          <div className="d-card-title">To Do <span style={{ color: 'var(--accent)', marginLeft: 6 }}>{pending.length}</span></div>
          {pending.length === 0
            ? <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>All clear!</p>
            : pendingGroups.map(([tag, items]) => (
                <div key={tag} className="task-group">
                  <div className="task-group-title">{tag}</div>
                  {items.map(task => <TaskItem key={task.id} task={task} onToggle={toggleComplete} />)}
                </div>
              ))
          }
        </div>

        {/* Done */}
        <div className="d-card">
          <div className="d-card-title">Done <span style={{ color: 'var(--success)', marginLeft: 6 }}>{done.length}</span></div>
          {done.length === 0
            ? <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Nothing completed yet.</p>
            : done.map(task => <TaskItem key={task.id} task={task} onToggle={toggleComplete} />)
          }
        </div>
      </div>
    </div>
  );
}

function groupByTag(tasks) {
  const groups = new Map();
  for (const task of tasks) {
    const tag = (task.tags && task.tags[0]) || 'Untagged';
    if (!groups.has(tag)) groups.set(tag, []);
    groups.get(tag).push(task);
  }
  return Array.from(groups.entries()).sort(([a], [b]) => {
    if (a.toLowerCase() === 'todo') return -1;
    if (b.toLowerCase() === 'todo') return 1;
    if (a === 'Untagged') return 1;
    if (b === 'Untagged') return -1;
    return a.localeCompare(b);
  });
}

function TaskItem({ task, onToggle }) {
  return (
    <div className={`task-item ${task.status === 'done' ? 'completed' : ''}`} onClick={() => onToggle(task)}>
      <div className="task-check">
        {task.status === 'done' && <i className="bi bi-check" style={{ fontSize: '0.75rem' }} />}
      </div>
      <div>
        <div className="task-text">{task.title || task.task || task.description || '(untitled)'}</div>
        {task.due_date && (
          <div className="task-meta">Due {new Date(task.due_date).toLocaleDateString()}</div>
        )}
      </div>
    </div>
  );
}
